import { FC, type JSX } from 'react';
import { IoMdCheckmark } from 'react-icons/io';
import { IoClose } from 'react-icons/io5';

import { Box, List, Text, Tooltip } from '@mantine/core';

import { SubscriptionFromSubscriptionType, SubscriptionType } from 'models';
import { SubscriptionConfigHelpers } from 'helpers/SubscriptionConfigHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';

import { ImagePreview } from 'components/image-preview/ImagePreview';

import cx from './SubscriptionFeatureList.module.scss';

type SubscriptionFeatureListProps<T extends SubscriptionType> = {
    type: T;
    subscription: SubscriptionFromSubscriptionType<T>;
    checkmark?: boolean;
};

const SubscriptionFeatureList = <T extends SubscriptionType>({
    type,
    subscription,
    checkmark = true,
}: SubscriptionFeatureListProps<T>) => {
    const { features } = SubscriptionConfigHelpers.getSubscriptionConfigByType(type)[subscription];

    const highlightedFeatures = features.filter((feature) => feature.highlight);
    const otherFeatures = features.filter((feature) => !feature.highlight && !feature.notIncluded);
    const notIncludedFeatures = features.filter((feature) => feature.notIncluded);

    const icon = checkmark ? (
        <IoMdCheckmark style={{ fill: '#32bf82' }} size={15} />
    ) : (
        <IoClose color="red" size={15} />
    );

    const groupHighlightedFeatures = SubscriptionHelpers.isSubscriptionType(SubscriptionType.CREDIT, subscription);

    if (!groupHighlightedFeatures) {
        return (
            <List spacing={24} center mt={10} p="xs">
                {features.map((feature) => (
                    <ListItem key={feature.title} {...feature} icon={feature.notIncluded ? <> </> : icon} />
                ))}
            </List>
        );
    }

    return (
        <List spacing={6} className={cx.listxxx}>
            {highlightedFeatures.map((feature) => (
                <ListItem key={feature.title} {...feature} icon={icon} highlight />
            ))}
            {otherFeatures.map((feature) => (
                <ListItem key={feature.title} {...feature} icon={icon} />
            ))}
            {notIncludedFeatures.map((feature) => (
                <ListItem key={feature.title} {...feature} icon={<IoClose color="red" />} notIncluded />
            ))}
        </List>
    );
};

const ListItem: FC<{
    id?: string;
    title: string;
    description?: string;
    previewImage?: string;
    highlight?: boolean;
    notIncluded?: boolean;
    icon: JSX.Element;
    marginTop?: number;
}> = ({ id, title, description, previewImage, highlight, icon, notIncluded }) => {
    return (
        <List.Item className={cx[id ?? '']} icon={icon}>
            <Tooltip
                maw={600}
                label={previewImage ? <ImagePreview src={previewImage} w={590} mih={300} /> : description}
                disabled={!description && !previewImage}
                p={previewImage ? 5 : undefined}
            >
                <Box>
                    <Text
                        style={{
                            display: 'inline',
                            cursor: description ? 'help' : 'initial',
                            borderBottom: title === '―' ? undefined : '1px dashed var(--mantine-color-gray-3)',
                        }}
                        fw={highlight ? 500 : undefined}
                        className={highlight ? 'gradient-cyan-green' : undefined}
                        c={notIncluded ? 'dimmed' : undefined}
                    >
                        {title}
                    </Text>
                    <Text c="dimmed" fz="xs">
                        {description}
                    </Text>
                </Box>
            </Tooltip>
        </List.Item>
    );
};

export { SubscriptionFeatureList };
